import {
  UserActivityGetters,
  UserActivityActions,
  UserActivityMutations
} from '@/app/features/user-activity/user-activity.keys';
import { userActivityService } from '@/app/features/user-activity/user-activity.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import i18n from '@/i18n';

const RECENT_ITEMS_MENU_LIMIT = 10;

export const getInitialState = () => {
  return {
    userActivityList: [],
    userActivityForMenu: [],
    userActivityFilteredRecordCount: 0,
    userActivitySortOrder: {
      sortBy: 'initiatedAt',
      sortDirection: 'desc'
    },
    userActivityPaginationParameters: {
      recordsPerPage: 10,
      offset: 0
    },
    userActivitySearchParam: '',
    userActivityLoading: true
  };
};

export const userActivityStore = {
  state: getInitialState(),
  getters: {
    [UserActivityGetters.UserActivityForMenu]: state => {
      return state.userActivityForMenu;
    },
    [UserActivityGetters.UserActivityList]: state => {
      return state.userActivityList;
    },
    [UserActivityGetters.SortBy]: state => {
      return state.userActivitySortOrder.sortBy;
    },
    [UserActivityGetters.SortDirection]: state => {
      return state.userActivitySortOrder.sortDirection;
    },
    [UserActivityGetters.UserActivityListingOffset]: state => {
      return state.userActivityPaginationParameters.offset;
    },
    [UserActivityGetters.UserActivityPerPage]: state => {
      return state.userActivityPaginationParameters.recordsPerPage;
    },
    [UserActivityGetters.UserActivityFilteredRecordCount]: state => {
      return state.userActivityFilteredRecordCount;
    }
  },
  actions: {
    [UserActivityActions.RetrieveUserActivityForMenu]: ({ commit }, userId) => {
      return userActivityService
        .retrieveUserActivity(userId, RECENT_ITEMS_MENU_LIMIT)
        .then(items => {
          commit(UserActivityMutations.SetUserActivityForMenu, items);
        })
        .catch(err => {
          throw err;
        });
    },
    [UserActivityActions.RetrieveUserActivityList]: ({ state, dispatch, commit }) => {
      return userActivityService
        .retrieveUserActivityLog(
          state.userActivityPaginationParameters.recordsPerPage,
          state.userActivityPaginationParameters.offset,
          state.userActivitySortOrder
        )
        .then(items => {
          dispatch(UserActivityActions.RetrieveUserActivityFilteredRecordCount);
          commit(UserActivityMutations.SetUserActivityList, items);
        })
        .catch(err => {
          throw err;
        });
    },
    [UserActivityActions.RetrieveUserActivityFilteredRecordCount]: ({
      commit,
      dispatch,
      state
    }) => {
      return userActivityService
        .retrieveFilteredRecordCount(state.userActivitySearchParam)
        .then(recordCount => {
          commit(UserActivityMutations.SetUserActivityFilteredRecordCount, recordCount);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('userActivity.listing.errors.retrieveUserActivity')
          });
        });
    },
    [UserActivityActions.FilterUserActivity]: ({ commit, dispatch }, params) => {
      commit(UserActivityMutations.SetUserActivityLoading);
      commit(UserActivityMutations.SetUserActivitySearchParam, params.searchParam);
      if (!params.searchParam) {
        dispatch(UserActivityActions.RetrieveUserActivityList);
      } else {
        dispatch(UserActivityActions.RetrieveUserActivityFilteredRecordCount)
          .then(() => {
            commit(UserActivityMutations.SetUserActivityList, params.data);
          })
          .then(() => commit(UserActivityMutations.SetUserActivityLoading, false));
      }
    },
    [UserActivityActions.SetUserActivityPaginationParameters]: ({ commit, dispatch }, params) => {
      commit(UserActivityMutations.SetUserActivityPaginationParameters, params.pagination);
      dispatch(UserActivityActions.RetrieveUserActivityList, params.userId);
    },
    [UserActivityActions.SetUserActivitySortOrder]: ({ commit, dispatch }, params) => {
      commit(UserActivityMutations.SetUserActivitySortOrder, params.sortOrder);
      commit(UserActivityMutations.ResetUserActivityPaginationOffset);
      dispatch(UserActivityActions.RetrieveUserActivityList, params.userId);
    },
    [UserActivityActions.DeleteUserActivity]: ({ dispatch }, params) => {
      return userActivityService
        .deleteUserActivity(params)
        .then(() => {
          dispatch(UserActivityActions.RetrieveUserActivityList, params.userId);
        })
        .catch(error => {
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('userActivity.listing.errors.deleteUserActivity')
          });
        });
    }
  },
  mutations: {
    [UserActivityMutations.SetUserActivityForMenu]: (state, items) => {
      state.userActivityForMenu = items;
    },
    [UserActivityMutations.SetUserActivityList]: (state, items) => {
      state.userActivityList = items;
    },
    [UserActivityMutations.SetUserActivityFilteredRecordCount]: (state, recordCount) => {
      state.userActivityFilteredRecordCount = recordCount;
    },
    [UserActivityMutations.SetUserActivitySortOrder]: (state, sortOrder) => {
      state.userActivitySortOrder = sortOrder;
    },
    [UserActivityMutations.SetUserActivityPaginationParameters]: (state, pagination) => {
      state.userActivityPaginationParameters = pagination;
    },
    [UserActivityMutations.ResetUserActivityPaginationOffset]: state => {
      state.userActivityPaginationParameters.offset = 0;
    },
    [UserActivityMutations.SetUserActivitySearchParam]: (state, payload) => {
      state.userActivitySearchParam = payload;
    },
    [UserActivityMutations.SetUserActivityLoading]: (state, isLoading = true) => {
      state.userActivityLoading = isLoading;
    }
  }
};
